import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Collapse,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Description as ReportIcon,
  Image as ImageIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { keystoneService, Study, Report, ScheduleStatus } from '../../services/keystoneService';
import { useAuthedBlobUrl, downloadAuthedBlob } from '../../hooks/useAuthedBlobUrl';
import { AuthedKeystoneImage } from './AuthedKeystoneImage';
import ModalityBadge from './ModalityBadge';

interface ImagingStudyCardProps {
  study?: Study;
  accessionNumber?: string;
  onRefresh?: () => void;
}

const statusColors: { [key: number]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
  1: 'default',    // Queued
  2: 'info',       // Scheduled
  3: 'warning',    // Started
  4: 'primary',    // Images Available
  5: 'primary',    // Images Pending Review
  6: 'success',    // Imaging Complete
  7: 'warning',    // Report In Progress
  8: 'warning',    // Report Pending Approval
  9: 'warning',    // Report Pending Rounds
  10: 'success',   // Report Complete
  11: 'default',   // Annex Queued
  12: 'error',     // Cancel Queued
  13: 'error',     // Cancelled
};

const statusNames: { [key: number]: string } = {
  1: 'Queued',
  2: 'Scheduled',
  3: 'In Progress',
  4: 'Images Available',
  5: 'Images Pending Review',
  6: 'Imaging Complete',
  7: 'Report In Progress',
  8: 'Report Pending Approval',
  9: 'Report Pending Rounds',
  10: 'Report Complete',
  11: 'Annex Queued',
  12: 'Cancel Queued',
  13: 'Cancelled',
};

const ImagingStudyCard: React.FC<ImagingStudyCardProps> = ({
  study: initialStudy,
  accessionNumber: initialAccession,
  onRefresh,
}) => {
  /*
   * INSTANTS throughout this card (DATE-KINDS §2.3, the Imaging/Keystone group).
   * `Study.studyDate` is written from DICOM `StudyDateTime`
   * (keystone-poller.service.ts:473) and `Report.reportDate` is the radiologist's
   * report stamp — real moments from the modality, so they render in the CLINIC's
   * zone, not whichever zone the viewing browser happens to sit in.
   *
   * ⚠ That kind rests on PRECEDENT, not on an observed wire sample: the poller
   * does `new Date(String(study.StudyDateTime))` and Asteris' shape is not
   * visible from this repo (DICOM dates are naive by spec, and the MWL half of
   * the same integration speaks naive local — asteris.service.ts:695-706). The
   * backend already renders these in the clinic zone (utils/pdf.service.ts:408),
   * so this agrees with the existing surface rather than inventing one. If a
   * logged raw `StudyDateTime` ever comes back naive, this is W7-class and BOTH
   * renders double-shift — see DATE-KINDS §2.3.
   */
  const formatInstant = useFormatInstant();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ScheduleStatus | null>(null);
  const [study, setStudy] = useState<Study | undefined>(initialStudy);
  const [reports, setReports] = useState<Report[]>([]);
  // viewerDialogOpen/viewerUrl removed — View Images now launches Keystone Omni in a new tab.
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Authenticated blob URL for the selected report's PDF — needed because
  // <iframe src> doesn't carry our bearer header. The hook returns null
  // until the bytes load (or if no PDF is selected); revokes on change.
  const reportPdfBlobUrl = useAuthedBlobUrl(
    reportDialogOpen && selectedReport?.pdf?.publicUrl ? selectedReport.pdf.publicUrl : null,
  );

  const accessionNumber = initialAccession || study?.accessionNumber;

  // Load status + full details eagerly once we know the accession, so the
  // collapsed card shows the live status chip (not the stale Queued default).
  React.useEffect(() => {
    if (accessionNumber && !status) {
      loadStudyDetails();
    }
  }, [accessionNumber]);

  const loadStudyDetails = async () => {
    if (!accessionNumber) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current status
      const statusData = await keystoneService.getScheduleStatus(accessionNumber);
      setStatus(statusData);
      
      // If we have a study ID, get full details and reports
      if (statusData.studyId) {
        const [studyData, reportsData] = await Promise.all([
          study ? Promise.resolve(study) : keystoneService.getStudy(statusData.studyId),
          keystoneService.getStudyReports(statusData.studyId),
        ]);
        
        setStudy(studyData);
        setReports(reportsData);
      }
    } catch (err: any) {
      console.error('Failed to load study details:', err);
      setError(err.response?.data?.message || 'Failed to load study details');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadStudyDetails();
    onRefresh?.();
  };

  // View Images is a plain <a target="_blank"> link bound to study.viewerUrl
  // (fetched eagerly by loadStudyDetails) — no click handler needed.

  const handleViewReport = (report: Report) => {
    setSelectedReport(report);
    setReportDialogOpen(true);
  };

  const getStatusInfo = () => {
    const statusId = status?.statusId || study?.studyStatusId || 1;
    return {
      name: statusNames[statusId] || 'Unknown',
      color: statusColors[statusId] || 'default',
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <ModalityBadge name={study?.modalityName} size={40} />

              <Box>
                <Typography variant="h6" component="div">
                  {study?.modalityName || 'Imaging Study'}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                  Accession: {accessionNumber}
                </Typography>
                {study?.patientId && (
                  <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                    Patient ID: {study.patientId}
                  </Typography>
                )}
                {study?.studyDate && (
                  <Typography variant="body2" color="textSecondary">
                    {formatInstant(study.studyDate, 'PPp')}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={statusInfo.name}
                color={statusInfo.color}
                size="small"
              />
              
              {status?.imagesAvailable && (
                <Chip 
                  icon={<ImageIcon />}
                  label="Images"
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
              
              {status?.reportAvailable && (
                <Chip 
                  icon={<ReportIcon />}
                  label="Report"
                  size="small"
                  variant="outlined"
                  color="success"
                />
              )}
              
              <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
              
              <IconButton
                onClick={() => setExpanded(!expanded)}
                size="small"
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* Quick Actions */}
          {(status?.imagesAvailable || status?.reportAvailable) && (
            <Box display="flex" gap={1} mt={2}>
              {status?.imagesAvailable && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ViewIcon />}
                  // Plain anchor → new tab. Avoids window.open() popup blockers
                  // and the `noopener` nulls-the-handle pitfall that was
                  // navigating the parent tab instead.
                  component="a"
                  href={study?.viewerUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={loading || !study?.viewerUrl}
                >
                  View Images
                </Button>
              )}
              
              {status?.reportAvailable && reports.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReportIcon />}
                  onClick={() => handleViewReport(reports[0])}
                  disabled={loading}
                >
                  View Report
                </Button>
              )}
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Expanded Details */}
          <Collapse in={expanded}>
            <Divider sx={{ my: 2 }} />
            
            {loading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Study Information */}
                {study && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Study Information
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {study.studyDescription && (
                        <Typography variant="body2">
                          Description: {study.studyDescription}
                        </Typography>
                      )}
                      {study.numberOfImages !== undefined && (
                        <Typography variant="body2">
                          Number of Images: {study.numberOfImages}
                        </Typography>
                      )}
                      {study.responsiblePerson && (
                        <Typography variant="body2">
                          Responsible: {study.responsiblePerson}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Thumbnails */}
                {study?.thumbnails && study.thumbnails.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Preview ({study.thumbnails.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 2 }}>
                      {study.thumbnails.map(t => (
                        <Box
                          key={t.attachmentId}
                          component="a"
                          href={study.viewerUrl || '#'}
                          target={study.viewerUrl ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          sx={{
                            display: 'block',
                            width: 96,
                            height: 96,
                            overflow: 'hidden',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: study.viewerUrl ? 'pointer' : 'default',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                        >
                          <AuthedKeystoneImage
                            publicUrl={t.publicUrl}
                            alt={`Thumbnail ${t.position ?? ''}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Reports list intentionally omitted — the header-level
                    "Report" chip + "View Report" quick-action already surface
                    the finalized report without a duplicate panel below. */}

                {/* Status Timeline */}
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Status Timeline
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="textSecondary">
                        Current Status: {statusInfo.name}
                      </Typography>
                    </Box>
                    {study?.studyDate && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="textSecondary">
                          Study Date: {formatInstant(study.studyDate, 'PPP')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Stack>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* View Images opens Keystone Omni in a new browser tab; no in-app modal. */}

      {/* Report Dialog — inline PDF viewer */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ py: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" component="span">
              Radiology Report
            </Typography>
            {selectedReport?.isFinalized && (
              <Chip label="Finalized" size="small" color="success" />
            )}
            {selectedReport?.reportDate && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {formatInstant(selectedReport.reportDate, 'PPp')}
              </Typography>
            )}
            {selectedReport?.reportingRadiologist && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}
              >
                by {selectedReport.reportingRadiologist}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
          {selectedReport?.pdf?.publicUrl ? (
            reportPdfBlobUrl ? (
              <iframe
                title="Radiology report PDF"
                src={`${reportPdfBlobUrl}#view=FitH`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  display: 'block',
                }}
              />
            ) : (
              <Box p={4} textAlign="center">
                <CircularProgress size={20} />
              </Box>
            )
          ) : (
            <Box p={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No PDF attached to this report yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedReport?.pdf?.publicUrl && (
            <Button
              onClick={() =>
                downloadAuthedBlob(
                  `${selectedReport.pdf!.publicUrl}?dl=1`,
                  selectedReport.pdf!.fileName,
                )
              }
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              Download PDF
            </Button>
          )}
          <Button onClick={() => setReportDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImagingStudyCard;
